import React, { useState } from 'react'
import { uuid } from 'uuidv4'
import { Tabs } from 'antd'
import { Result, Skeleton } from 'antd'
import OrderSteps from './OrderSteps'
import OrderActions from './OrderActions'
import {
  ChargesInformation,
  BasicInformation,
  DeliveryInformation,
  PaymentInformation,
  PickupInformation,
  StatusLog
} from './Tab'
import { OrderInfo } from './type'
import RefundForm from 'components/Orders/RefundForm'
import styled from '@emotion/styled'
import { OrderDrawerFooterNote } from '../styles'

interface OrderInformationProps {
  order: OrderInfo
  refetchCallback: () => void
  setFooterEntries?: (entries: React.ReactNode | React.ReactNode[]) => void
  isLoading?: boolean
}

const { TabPane } = Tabs
const OrderInformation = ({
  order,
  refetchCallback,
  setFooterEntries,
  isLoading
}: OrderInformationProps) => {
  const [showRefundForm, setShowRefundForm] = useState<boolean>(false)

  const handleTabsChange = (activeKey: string) => {
    if (!setFooterEntries) {
      return
    }

    if (activeKey === '4') {
      setFooterEntries(
        <OrderDrawerFooterNote>
          * Download the Additional Charges report for more details
        </OrderDrawerFooterNote>
      )

      return
    }

    setFooterEntries(null)
  }

  if (isLoading) {
    return <Skeleton active />
  }

  return (
    <>
      {!order && <Result status='500' title='500' />}
      {order && (
        <>
          <div className='order-header' data-testid='order-info-steps-actions'>
            <OrderSteps order={order} />
            <OrderActions
              order={order}
              successCallback={() => {
                refetchCallback()
              }}
              setShowRefundForm={setShowRefundForm}
            />
          </div>
          {showRefundForm && (
            <RefundForm
              key={uuid()}
              discardCallback={() => setShowRefundForm(false)}
              transactionId={order.transaction_id}
              //@ts-ignore
              deliveries={[order.delivery, ...order.additional_couriers]}
              successCallback={() => {
                setShowRefundForm(false)
                refetchCallback()
              }}
            />
          )}
          <StyledTabs
            className='_mt-24'
            defaultActiveKey='1'
            data-testid='order-info-tabs'
            onChange={handleTabsChange}
          >
            <TabPane tab='Info' key='1'>
              <BasicInformation
                order={order}
                reloadOrder={() => {
                  refetchCallback()
                }}
              />
            </TabPane>
            {order.fulfillment_type === 'delivery' && (
              <TabPane
                tab='Delivery'
                key='2'
                data-testid='order-info-tabs-delivery'
              >
                <DeliveryInformation
                  order={order}
                  reloadOrder={() => {
                    refetchCallback()
                  }}
                />
              </TabPane>
            )}
            {order.fulfillment_type === 'pickup' && (
              <TabPane tab='Pickup' key='2'>
                <PickupInformation
                  order={order}
                  reloadOrder={() => {
                    refetchCallback()
                  }}
                />
              </TabPane>
            )}
            <TabPane tab='Payment' key='3'>
              <PaymentInformation
                order={order}
                reloadOrder={() => {
                  refetchCallback()
                }}
              />
            </TabPane>
            <TabPane tab='Additional Charges' key='4'>
              <ChargesInformation
                order={order}
                reloadOrder={() => {
                  refetchCallback()
                }}
              />
            </TabPane>
            <TabPane tab='Order Log' key='5'>
              <StatusLog
                order={order}
                reloadOrder={() => {
                  refetchCallback()
                }}
              />
            </TabPane>
          </StyledTabs>
        </>
      )}
    </>
  )
}

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    margin: 0;
  }
`

export default OrderInformation
